// packages/core/src/learning/school-modules/options-derivatives.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'options-derivatives' school. LearningModule,
// surface 'school'.
//   module id   = 'school-options-derivatives' (matches exhibit nativeModuleId)
//   homeSchool  = 'options-derivatives'
// PROMOTED: five authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_OPTIONS_DERIVATIVES_MODULE: LearningModule = {
	id: 'school-options-derivatives',
	title: 'Options & Derivatives',
	description:
		'The lens that sees risk as shape — contracts not magic, leverage that amplifies pressure, income as risk sold, hedges as tradeoffs, and where complexity disguises speculation.',
	emoji: '⚖️',
	accentColor: '#d97a1f',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'options-derivatives',
	lessons: [
		{
			id: 'school-options-derivatives-01',
			title: 'Options are contracts, not magic',
			description:
				'This lesson teaches how options and derivatives sees markets through contracts, payoff shapes, and risk transfer.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['options-derivatives'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['options-derivatives', 'contract-exposure', 'risk-transfer'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['contract exposure', 'payoff shape', 'risk transfer'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Options can look like a faster version of investing, but this school starts with a different idea. An option is a contract with rules, rights, deadlines, and tradeoffs.',
				},
				{
					type: 'paragraph',
					text: 'Options and derivatives is paying attention to how risk can be shaped. Instead of only owning an asset directly, a person may use a contract that behaves differently depending on price, time, volatility, and conditions.',
				},
				{
					type: 'bullet_list',
					items: [
						'Contracts have rules',
						'Payoffs have shapes',
						'Risk moves, it does not disappear',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating an option like a cheaper stock.',
				},
				{
					type: 'example',
					text: 'Buying a stock gives direct ownership exposure. Buying an option gives contract exposure with expiration, strike price, and pricing behavior that can change even when the stock moves in the direction someone expected.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is complexity. A contract can create precision, but it can also hide how losses happen. Losses are possible, and the contract can expire with little or no value.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens starts with risk shape, not excitement. You are learning how this worldview interprets exposure before deciding whether the complexity belongs anywhere near your life.',
				},
				{
					type: 'key_takeaway',
					text: 'Options are not shortcuts. They are contracts that reshape risk.',
				},
			],
			audioScript:
				'Options and derivatives make the most sense when you stop thinking of them as magic market buttons. The first thing to understand is that they are contracts. A contract has rules. It has terms. It has a deadline. It has a payoff structure. It behaves differently from simply owning an asset.\n\nThat is the center of the options and derivatives worldview. It is not only asking, “Will the price go up or down?” It is asking, “What kind of exposure do I have? What happens if the price moves a little? What happens if it moves a lot? What happens if time passes? What happens if volatility changes? What happens if liquidity disappears when I want to exit?”\n\nThis lens is powerful because it sees that market risk is not one simple thing. Direct ownership gives one kind of exposure. A call option gives another kind. A put option gives another. A spread gives another. A futures contract gives another. A hedge gives another. Each contract can reshape the relationship between price movement and outcome.\n\nBut that same power is the danger. Beginners often treat options like cheaper versions of stocks. They see that controlling an option costs less than buying shares, then assume they found a smarter way to get the same exposure. That is not true. They did not buy the same thing for less. They bought a different thing with different risks.\n\nFor example, a stock can move in the direction someone expected, but an option can still disappoint because of time decay, volatility changes, or poor entry price. The direction may be right, while the contract result is still wrong. That surprises people because they were thinking like owners, not contract holders.\n\nThe contract can also expire. That one fact changes everything. Direct ownership may go down and still remain an asset someone holds. An option can expire with little or no value. The clock matters. The terms matter. The structure matters.\n\nInside Nisk, this is why options and derivatives should be taught with caution. The point is not to make them sound exciting. The point is to make the risk visible. This worldview teaches that exposure can be engineered, but engineered exposure is not automatically safer. It may be more precise, or it may be more fragile.\n\nThe mature version of this lens asks clear questions. What exactly is the contract? What risk am I taking? What risk am I reducing? What condition makes this fail? What happens if I am right about direction but wrong about timing? What happens if volatility changes? What happens if I cannot exit cleanly?\n\nThat is very different from saying, “Options let me make money faster.” That sentence is usually a warning sign.\n\nThe takeaway is simple. Options are not shortcuts. They are contracts that reshape risk. If you do not understand the contract, you do not understand the risk.',
			flashcards: [
				{
					id: 'school-options-derivatives-01-fc-1',
					role: 'main_idea',
					front: 'What are options first?',
					back: 'Contracts with rules, deadlines, payoff shapes, and risk.',
				},
				{
					id: 'school-options-derivatives-01-fc-2',
					role: 'watch_out',
					front: 'What is the cheap stock trap?',
					back: 'Treating an option like the same exposure as stock ownership.',
				},
				{
					id: 'school-options-derivatives-01-fc-3',
					role: 'use_it',
					front: 'What should you ask first?',
					back: 'Ask what the contract does when price, time, and volatility change.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Name the contract risk',
				prompt: 'Write how a contract can lose money even when the basic market idea sounds right.',
			},
			revision: 1,
		},
		{
			id: 'school-options-derivatives-02',
			title: 'Leverage makes mistakes louder',
			description:
				'This lesson teaches how options and derivatives treats leverage as both a tool and a source of fast loss.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['options-derivatives'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['options-derivatives', 'leverage', 'fast-losses'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'leverage amplification',
				'fast losses',
				'exposure size',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Leverage feels attractive because it can make a small amount of money control a larger exposure. That is also exactly why it can become dangerous.',
				},
				{
					type: 'paragraph',
					text: 'Options and derivatives is paying attention to how contracts can magnify outcomes. Leverage can increase upside, but it can also speed up losses, stress, and forced decisions.',
				},
				{
					type: 'bullet_list',
					items: [
						'Exposure can exceed cash',
						'Losses can move fast',
						'Pressure changes behavior',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is measuring risk by the entry cost instead of the exposure.',
				},
				{
					type: 'example',
					text: 'Someone may spend a small amount on a contract and think the risk is small. But if they keep repeating that behavior, size up too quickly, or use margin, losses can stack faster than expected.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is confidence. Leverage can make a person feel skilled during favorable conditions, then punish the same habits when conditions change.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to runway. If a loss would damage your stability, your attention, or your ability to keep learning, the real risk is larger than the chart suggests.',
				},
				{
					type: 'key_takeaway',
					text: 'Leverage does not just increase outcomes. It increases pressure.',
				},
			],
			audioScript:
				'Leverage is one of the main reasons options and derivatives attract attention. It can make a small amount of money control a much larger exposure. That sounds efficient. It can also be dangerous.\n\nThe basic idea is simple. With direct ownership, the amount you put in is closely tied to the amount of exposure you have. With many derivatives, the exposure can be much larger than the cash used to enter the position. That can make gains look powerful when the move goes right. But it also makes mistakes louder when the move goes wrong.\n\nThis is why options and derivatives is a risk heavy school. It teaches that size is not only about the money you paid upfront. Size is also about exposure, conditions, margin, timing, and how the contract behaves under stress.\n\nA beginner may buy an option for a small amount and think, “I can only lose what I paid.” In some cases, that may be true for that one contract. But the real behavior risk can be bigger. They may repeat the bet many times. They may size up after a win. They may use margin. They may move from buying contracts to selling contracts without understanding the downside. They may confuse a limited upfront cost with a stable process.\n\nLeverage also changes psychology. A normal market move can feel huge when the exposure is amplified. The person watches every tick. They react faster. They feel smarter when the trade works and more desperate when it fails. Pressure can turn a learning exercise into emotional gambling.\n\nThat is the part people underestimate. Leverage does not only affect math. It affects behavior. It makes patience harder. It makes mistakes more expensive. It makes overconfidence easier during good conditions. It can make someone believe they found a skill when they may have only benefited from a favorable environment.\n\nThis school is not saying leverage is always wrong. It is saying leverage must be respected. A hedge can use derivatives. A business can manage risk with derivatives. A professional strategy can use contracts with controls, sizing, and risk management. But that is different from a normal user treating leverage as a shortcut to wealth.\n\nInside Nisk, the first question is not, “How much could this make?” The first question is, “What does this risk do to my real life if I am wrong?” If a loss would damage your rent money, your emergency runway, your focus, your sleep, or your ability to keep building stability, the risk is not small just because the contract looked small.\n\nLeverage also creates forced decisions. If the position moves against you, you may not get to wait calmly. Time, margin, and volatility can pressure action. That pressure is part of the cost.\n\nThe takeaway is direct. Leverage does not just increase outcomes. It increases pressure. A small contract can carry a large emotional and financial footprint.',
			flashcards: [
				{
					id: 'school-options-derivatives-02-fc-1',
					role: 'main_idea',
					front: 'What does leverage amplify?',
					back: 'Exposure, gains, losses, pressure, and mistakes.',
				},
				{
					id: 'school-options-derivatives-02-fc-2',
					role: 'watch_out',
					front: 'What is the entry cost trap?',
					back: 'Measuring risk only by what you paid upfront.',
				},
				{
					id: 'school-options-derivatives-02-fc-3',
					role: 'use_it',
					front: 'What should you check first?',
					back: 'Check whether the possible loss fits your runway and stability.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
			revision: 1,
		},
		{
			id: 'school-options-derivatives-03',
			title: 'Income is often risk sold',
			description:
				'This lesson teaches how options income strategies can feel steady while hiding exposure to painful outcomes.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['options-derivatives'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: [
				'options-derivatives',
				'premium-income-risk',
				'short-volatility',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'premium income risk',
				'short volatility',
				'hidden downside',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Options income sounds calm because the word income feels safe. This school warns that premium is often payment for taking a risk someone else wants to transfer.',
				},
				{
					type: 'paragraph',
					text: 'Options and derivatives sees income strategies as risk trades. Selling a contract may collect premium, but the seller is accepting an obligation that can become painful under the wrong conditions.',
				},
				{
					type: 'bullet_list',
					items: [
						'Premium is compensation',
						'Obligations matter',
						'Quiet markets can mislead',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating premium like dependable cash flow.',
				},
				{
					type: 'example',
					text: 'Someone sells options and collects small premiums for months. Then one sharp market move creates losses larger than many previous wins. The income was real, but the hidden risk was real too.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is calm conditions. A strategy can look safe when markets are quiet, then reveal its true risk when volatility returns.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to writing the loss path. Before any income story sounds attractive, name what event makes the strategy hurt.',
				},
				{
					type: 'key_takeaway',
					text: 'In options, income often means you are being paid to hold someone else’s risk.',
				},
			],
			audioScript:
				'Options income strategies can sound very attractive because the word income feels safe. People hear “collect premium” and imagine steady cash flow. But options and derivatives looks at that more carefully. Premium is not free money. It is usually compensation for taking a risk.\n\nWhen someone sells an option, they may receive money upfront. That is the premium. But in exchange, they accept an obligation. That obligation may stay manageable in normal conditions. It may even feel easy for a while. Then the wrong market move, the wrong timing, or the wrong volatility shift can make the strategy painful.\n\nThis is the core idea: income is often risk sold.\n\nThat does not mean every premium strategy is foolish. It means the person needs to understand what risk they are selling. Are they taking direction risk? Volatility risk? Assignment risk? Liquidity risk? Gap risk? Margin risk? Are they prepared for the outcome that creates the loss?\n\nA simple pattern shows the danger. Someone sells options and collects small premiums repeatedly. It works for weeks or months. They start to feel skilled. The market stays calm. The money arrives. Then a sudden move happens. The losses from one event can erase many previous premiums. The person realizes the strategy was never just income. It was a risk trade that paid small amounts in exchange for exposure to a larger event.\n\nThis is common in many areas of finance. A strategy looks stable because the bad condition has not happened yet. The absence of pain is mistaken for proof of safety. But risk does not disappear just because it is quiet. Sometimes risk is waiting.\n\nOptions and derivatives teaches that payoff shape matters. Some strategies make frequent small gains and rare large losses. Some strategies lose small amounts often and occasionally win big. Some strategies protect against one kind of move but suffer from another. The headline return does not tell the whole story.\n\nThe mistake is thinking, “I am generating income.” The better question is, “What risk am I being paid to carry?” That question changes the whole tone. It forces humility. It makes the hidden tradeoff visible.\n\nInside Nisk, this is why the lesson points to writing the loss path. Before an options income strategy sounds appealing, describe the event that hurts it. What market move causes trouble? What happens if volatility jumps? What happens if liquidity dries up? What happens if assignment occurs? What happens if the strategy needs more capital than expected?\n\nLosses are possible. Outcomes are not dependable. Premium can feel steady until it is not.\n\nThe takeaway is simple. In options, income often means you are being paid to hold someone else’s risk. That risk may be acceptable in some contexts, but it should never be invisible.',
			flashcards: [
				{
					id: 'school-options-derivatives-03-fc-1',
					role: 'main_idea',
					front: 'What is premium often paying for?',
					back: 'It often pays someone to accept a specific risk or obligation.',
				},
				{
					id: 'school-options-derivatives-03-fc-2',
					role: 'watch_out',
					front: 'What is the income trap?',
					back: 'Treating premium like dependable cash flow without seeing the loss path.',
				},
				{
					id: 'school-options-derivatives-03-fc-3',
					role: 'use_it',
					front: 'What should you write first?',
					back: 'Write the event that makes the income strategy lose money.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Write the loss path',
				prompt: 'Write the market event that would make an options income strategy hurt.',
			},
			revision: 1,
		},
		{
			id: 'school-options-derivatives-04',
			title: 'Hedging trades one risk for another',
			description:
				'This lesson teaches how options and derivatives can protect against some outcomes while creating cost, timing, and complexity tradeoffs.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['options-derivatives'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: [
				'options-derivatives',
				'hedge-tradeoff',
				'risk-substitution',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'hedge tradeoff',
				'protection cost',
				'risk substitution',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Hedging sounds like safety, but a hedge is not a magic shield. It is a tradeoff.',
				},
				{
					type: 'paragraph',
					text: 'Options and derivatives believes contracts can protect against certain outcomes. But protection can cost money, reduce upside, expire too soon, or fail to match the exact risk being hedged.',
				},
				{
					type: 'bullet_list',
					items: [
						'Protection has cost',
						'Timing matters',
						'The hedge may not fit perfectly',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is assuming hedged means safe.',
				},
				{
					type: 'example',
					text: 'Someone buys protection against a market drop. If the drop never comes, the protection may lose value. If the drop comes after the contract expires, the protection may not help. If the hedge is too small, the loss may still hurt.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is comfort. A hedge can make a person feel protected while leaving important risks untouched.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to identifying the actual risk. Before thinking about protection, name what you are protecting against and what you are willing to pay for it.',
				},
				{
					type: 'key_takeaway',
					text: 'A hedge does not erase risk. It changes which risk you carry.',
				},
			],
			audioScript:
				'Hedging is one of the more respectable reasons people use options and derivatives. The idea is not always speculation. Sometimes the goal is protection. A person, business, or portfolio may use a contract to reduce damage from a specific event.\n\nThat sounds safe, but the real lesson is more careful. A hedge is not a magic shield. It is a tradeoff. It can reduce one risk while creating or revealing another.\n\nOptions and derivatives is useful here because it sees risk in layers. You may want protection against a price drop. You may want protection against currency movement. You may want protection against interest rate changes. You may want protection against volatility. A derivative can sometimes shape that exposure more precisely than direct ownership can.\n\nBut protection has cost. If you buy an option for protection and the bad event never happens, the option may lose value. That cost might be acceptable, just like insurance can be acceptable even if you never use it. But it is still a cost.\n\nTiming matters too. A hedge can expire before the risk shows up. You can be right about the danger but wrong about the timing. That can make the protection fail when you thought you were being cautious.\n\nFit matters as well. A hedge may not match the exact thing you are worried about. Maybe it protects against a broad market drop, while your actual risk is concentrated in one company, one job, one industry, or one real estate decision. Maybe the hedge is too small. Maybe it protects price but not liquidity. Maybe it protects a number on paper but does not protect the emotional pressure of watching losses.\n\nThis is why “hedged” does not mean safe. It means some risk has been changed. Maybe reduced. Maybe moved. Maybe transformed into a cost. Maybe replaced with a different risk.\n\nA simple example: someone buys protection against a market drop for three months. If the market drops during that window, the hedge may help. If the market rises, the hedge may lose value. If the market drops six months later, the hedge may be gone. If the drop is in a different asset than the hedge covers, the protection may be incomplete.\n\nInside Nisk, this lesson matters because people use the idea of protection in their personal lives too. They think an emergency fund protects them, but it may not protect against every problem. They think insurance protects them, but coverage has rules. They think a backup plan protects them, but it may fail if the backup depends on the same fragile system.\n\nThe options and derivatives lens teaches precision. What risk are you trying to reduce? What are you paying? What risk remains? What new risk appears? What happens if the timing is wrong?\n\nThe takeaway is simple. A hedge does not erase risk. It changes which risk you carry. That can be useful, but only if you understand the exchange.',
			flashcards: [
				{
					id: 'school-options-derivatives-04-fc-1',
					role: 'main_idea',
					front: 'What does a hedge do?',
					back: 'It can reduce one risk while adding cost, timing risk, or mismatch risk.',
				},
				{
					id: 'school-options-derivatives-04-fc-2',
					role: 'watch_out',
					front: 'What does hedged not mean?',
					back: 'It does not mean safe or fully protected.',
				},
				{
					id: 'school-options-derivatives-04-fc-3',
					role: 'use_it',
					front: 'What should you define first?',
					back: 'Define the exact risk you are trying to protect against.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Name the risk',
				prompt: 'Write one risk you want protection from, then write what risk would still remain.',
			},
			revision: 1,
		},
		{
			id: 'school-options-derivatives-05',
			title: 'Where options and derivatives break',
			description:
				'This lesson teaches how options and derivatives can turn into overconfidence, hidden leverage, and speculation dressed up as strategy.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['options-derivatives'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 5,
			tags: ['options-derivatives', 'complexity-illusion', 'blowup-risk'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'complexity illusion',
				'speculation disguise',
				'blowup risk',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Options and derivatives breaks when complexity makes a person feel safer than they really are.',
				},
				{
					type: 'paragraph',
					text: 'This school is useful because it shows that risk has shape. But it can go wrong when charts, Greeks, premiums, and strategy names make speculation look controlled.',
				},
				{
					type: 'bullet_list',
					items: [
						'Complex does not mean safe',
						'Losses can exceed expectations',
						'Strategy names can hide risk',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is trusting the structure before understanding the failure mode.',
				},
				{
					type: 'example',
					text: 'A strategy may show limited losses in a simple diagram, but real markets can add liquidity problems, margin pressure, volatility jumps, assignment risk, and emotional mistakes.',
				},
				{
					type: 'paragraph',
					text: 'This is speculation when the user does not fully understand the contract, the downside, the conditions, and the possible loss path. Outcomes are not reliable, and losses are possible.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should end with humility. If you cannot explain how the strategy fails, you do not understand the strategy.',
				},
				{
					type: 'key_takeaway',
					text: 'Options and derivatives are strongest when complexity is matched with humility.',
				},
			],
			audioScript:
				'Options and derivatives breaks when complexity creates confidence instead of caution.\n\nThis is the biggest danger of the school. The language can sound advanced. Greeks. Spreads. Hedges. Premium. Volatility. Assignment. Collateral. Margin. Payoff diagrams. Strategy names. The words can make a person feel like they are no longer speculating. They are using a strategy.\n\nBut a strategy name does not remove risk. A payoff diagram does not capture every real world condition. A model does not remove uncertainty. Complexity can organize risk, but it can also hide risk.\n\nThat is the point where options and derivatives become dangerous for normal users. Someone learns just enough to feel smart, then starts taking exposure they do not fully understand. They may know what the strategy is called, but not how it fails. They may know the best case, but not the stress case. They may understand the entry, but not the exit. They may know the premium, but not the obligation. They may know the maximum loss in theory, but not how margin, liquidity, volatility, and behavior can affect the real experience.\n\nThis is speculation when the user does not fully understand the contract, the downside, the conditions, and the possible loss path. Losses are possible. Outcomes are not reliable. A strategy can work for a while, then fail quickly when the environment changes.\n\nReal markets are messier than simple examples. Liquidity can get worse. Bid and ask spreads can widen. Volatility can jump. Prices can gap. Assignment can surprise people. Margin requirements can change. A person can panic and exit at the worst time. A broker platform can behave differently under stress than it does on a calm day. The strategy that looked clean in a screenshot may feel completely different when real money is moving.\n\nThe emotional side matters too. Options can create fast feedback. That can become addictive. Wins feel like skill. Losses feel like something to win back. The person keeps adjusting, rolling, doubling down, or trying another setup. What began as education becomes a pressure loop.\n\nInside Nisk, this lens has to end with a hard boundary. If you cannot explain how the strategy fails, you do not understand the strategy. If you cannot explain what happens when the market moves sharply against you, you do not understand the risk. If you cannot explain the role of time, volatility, liquidity, and obligations, you are not dealing with a simple investment. You are dealing with a contract you may not be ready for.\n\nThis does not mean options and derivatives have no legitimate use. They can be used for hedging, risk transfer, structured exposure, and professional strategies. But their legitimacy does not make every use wise. A tool that professionals use can still harm a beginner who does not understand the failure modes.\n\nThe takeaway is direct. Options and derivatives are strongest when complexity is matched with humility. Without humility, complexity becomes a costume for risk.',
			flashcards: [
				{
					id: 'school-options-derivatives-05-fc-1',
					role: 'main_idea',
					front: 'Where does this school break?',
					back: 'When complexity makes speculation look controlled.',
				},
				{
					id: 'school-options-derivatives-05-fc-2',
					role: 'watch_out',
					front: 'What must you understand?',
					back: 'The contract, downside, conditions, obligations, and failure mode.',
				},
				{
					id: 'school-options-derivatives-05-fc-3',
					role: 'use_it',
					front: 'What is the hard boundary?',
					back: 'If you cannot explain how it fails, you do not understand it.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Explain the failure mode',
				prompt: 'Write how a complex strategy fails before writing why it looks attractive.',
			},
			revision: 1,
		},
	],
};
